import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useSkeleton from "../../../../hooks/useSkeleton";

import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { classNames } from "primereact/utils";

import helperFormats from "../../../../utils/helperFormats";

import "react-toastify/dist/ReactToastify.css";

import { useAtom } from "jotai";
import { loadingState } from "../../../../states/loadingState";
import { toastState } from "../../../../states/toastState";

const RegistrationsForm = ({ settings }) => {
  const [isLoading, setIsLoading] = useAtom(loadingState);
  const [toastConfig, setToastConfig] = useAtom(toastState);

  const s = useSkeleton("abm");
  const { t } = useTranslation();

  const isEditMode = settings.formMode === "UPDATE";
  const isDeleteMode = settings.formMode === "DELETE";

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      createdDate: 0,
      documentType: 0,
      documentNumber: "",
      name: "",
      lastName: "",
      phone: "",
      address: "",
      answerDate: 0,
      answer: 0,
    },
  });

  const [registrationApproved, setRegistrationApproved] = useState(true);
  let passwordMode = "HIDDEN"; // "UPDATE", "VISIBLE"

  const formModeConfig = {};

  if (isEditMode) {
    formModeConfig.SubmitButtonText = t("buttons.update");
    formModeConfig.SubmitButtonTextWait = `${t("buttons.updating")}...`;
    formModeConfig.serverAction = async (data) => {
      data.documentType = 1;
      await settings.CRUDService.updateEntity(data);
    };
  } else if (isDeleteMode) {
    formModeConfig.SubmitButtonText = t("buttons.delete");
    formModeConfig.SubmitButtonTextWait = `${t("buttons.deleting")}...`;
    formModeConfig.serverAction = async () => {
      await settings.CRUDService.deleteEntity(settings.entityId);
    };
  } else {
    formModeConfig.SubmitButtonText = t("buttons.create");
    formModeConfig.SubmitButtonTextWait = `${t("buttons.creating")}...`;
    formModeConfig.serverAction = async (data) => {
      data.documentType = 1;
      await settings.CRUDService.createEntity(data);
    };
  }

  useEffect(() => {
    if (isEditMode || isDeleteMode) {
      const fetchData = async () => {
        try {
          const data = await settings.CRUDService.getEntity(settings.entityId);

          data.createdDate = data.createdDate
            ? helperFormats.formatDate(new Date(data.createdDate))
            : data.createdDate;

          data.answerDate = data.answerDate
            ? helperFormats.formatDate(new Date(data.answerDate))
            : data.answerDate;

          for (const key in data) {
            setValue(key, data[key]);
          }

          setRegistrationApproved(data.answer);
        } catch (error) {
          console.error(t("forms.messages.operation.error"), error);
        }
      };

      fetchData();
    } else {
      setRegistrationApproved(false);
    }
  }, [settings.entityId, setValue, settings.formMode]);

  const handlePassword = (e) => {
    e.preventDefault();
    passwordMode = "VISIBLE";
  };

  const [isVisible, setIsVisible] = useState(passwordMode == "VISIBLE");

  const handleShow = (e) => {
    e.preventDefault();
    setIsVisible(!isVisible);
  };

  const handleHide = () => {
    setIsVisible(false);
  };

  const onSubmit = async (data) => {
    setIsLoading(true);

    try {
      await formModeConfig.serverAction(data);

      settings.hideDialog();

      setToastConfig({
        severity: "success",
        summary: t("forms.messages.titles.success"),
        detail: t("forms.messages.operation.success"),
        life: 3000,
        visible: true,
      });
    } catch (error) {
      setToastConfig({
        severity: "error",
        summary: t("forms.messages.titles.error"),
        detail: t("forms.messages.operation.error"),
        life: 3000,
        visible: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getFormErrorMessage = (error) => {
    return error && <small className="p-error">{error.message}</small>;
  };

  return (
    <React.Fragment>
      <div className={s.get()}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={`${s.get("form")} ${settings.formMode !== "DELETE" ? "abm-create-form" : ""}`}
        >
          {settings.formMode == "UPDATE" && (
            <Controller
              name="createdDate"
              control={control}
              rules={{
                required: t("forms.validation.required", {
                  entity: t("entities.registrations.createdDate"),
                }),
              }}
              render={({ field }) => (
                <div className={s.get("form.field")}>
                  <div className={s.get("form.field.label")}>
                    <label htmlFor="createdDate">
                      {t("entities.registrations.createdDate")}
                    </label>
                  </div>
                  <div className={s.get("form.field.value")}>
                    <div className={s.get("form.field.value.error")}>
                      {getFormErrorMessage(errors.createdDate)}
                    </div>
                    <div className={s.get("form.field.value.control")}>
                      <InputText
                        id="documentNumber"
                        {...field}
                        value={field.value}
                        maxLength={100}
                        className={["form-control"]}
                        readOnly
                      />
                    </div>
                  </div>
                </div>
              )}
            />
          )}

          <Controller
            name="documentNumber"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.registrations.documentNumber"),
              }),
              maxLength: {
                value: 50,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.registrations.documentNumber"),
                  maxLength: 50,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="documentNumber">
                    {t("entities.registrations.documentNumber")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.documentNumber)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="documentNumber"
                      {...field}
                      value={field.value}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      readOnly={
                        settings.formMode == "DELETE" || registrationApproved
                      }
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="name"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.registrations.name"),
              }),
              maxLength: {
                value: 50,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.registrations.name"),
                  maxLength: 50,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="name">
                    {t("entities.registrations.name")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.name)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="name"
                      {...field}
                      value={field.value}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      readOnly={
                        settings.formMode == "DELETE" || registrationApproved
                      }
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="lastName"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.registrations.lastName"),
              }),
              maxLength: {
                value: 50,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.registrations.lastName"),
                  maxLength: 50,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="lastName">
                    {t("entities.registrations.lastName")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.lastName)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="lastName"
                      {...field}
                      value={field.value}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      readOnly={
                        settings.formMode == "DELETE" || registrationApproved
                      }
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="phone"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.registrations.phone"),
              }),
              maxLength: {
                value: 50,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.registrations.phone"),
                  maxLength: 50,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="phone">
                    {t("entities.registrations.phone")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.phone)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="phone"
                      {...field}
                      value={field.value}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      readOnly={
                        settings.formMode == "DELETE" || registrationApproved
                      }
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <Controller
            name="address"
            control={control}
            rules={{
              required: t("forms.validation.required", {
                entity: t("entities.registrations.address"),
              }),
              maxLength: {
                value: 500,
                message: t("forms.validation.maxLength", {
                  entity: t("entities.registrations.address"),
                  maxLength: 500,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={s.get("form.field")}>
                <div className={s.get("form.field.label")}>
                  <label htmlFor="address">
                    {t("entities.registrations.address")}
                  </label>
                </div>
                <div className={s.get("form.field.value")}>
                  <div className={s.get("form.field.value.error")}>
                    {getFormErrorMessage(errors.address)}
                  </div>
                  <div className={s.get("form.field.value.control")}>
                    <InputText
                      id="address"
                      {...field}
                      value={field.value}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      readOnly={
                        settings.formMode == "DELETE" || registrationApproved
                      }
                    />
                  </div>
                </div>
              </div>
            )}
          />

          {settings.formMode == "UPDATE" && registrationApproved && (
            <Controller
              name="answerDate"
              control={control}
              rules={{}}
              render={({ field }) => (
                <div className={s.get("form.field")}>
                  <div className={s.get("form.field.label")}>
                    <label htmlFor="answerDate">
                      {t("entities.registrations.answerDate")}
                    </label>
                  </div>
                  <div className={s.get("form.field.value")}>
                    <div className={s.get("form.field.value.error")}>
                      {getFormErrorMessage(errors.answerDate)}
                    </div>
                    <div className={s.get("form.field.value.control")}>
                      <InputText
                        id="documentNumber"
                        {...field}
                        value={field.value}
                        maxLength={100}
                        className={["form-control"]}
                        readOnly
                      />
                    </div>
                  </div>
                </div>
              )}
            />
          )}

          {settings.formMode == "UPDATE" && (
            <Controller
              name="answer"
              control={control}
              rules={{}}
              render={({ field }) => (
                <div className={s.get("form.field")}>
                  <div className={s.get("form.field.label")}>
                    <label htmlFor="answer">
                      {t("entities.registrations.answer")}
                    </label>
                  </div>
                  <div className={s.get("form.field.value")}>
                    <div className={s.get("form.field.value.error")}>
                      {getFormErrorMessage(errors.answer)}
                    </div>
                    <div className={s.get("form.field.value.control")}>
                      <Checkbox
                        inputId="answer"
                        onChange={(e) => field.onChange(e.checked)}
                        checked={field.value}
                        disabled={
                          settings.formMode == "DELETE" || registrationApproved
                        }
                        className="flex mt-2"
                      />
                    </div>
                  </div>
                </div>
              )}
            />
          )}
          <div className="flex justify-content-between mt-6 mb-2">
            <Button
              label={t("buttons.cancel")}
              icon="pi pi-times"
              className="p-button-text btn-secondary-crud"
              onClick={() => settings.hideDialog("CANCEL")}
              type="button"
              disabled={isLoading}
            />
            <Button
              label={
                isLoading
                  ? formModeConfig.SubmitButtonTextWait
                  : formModeConfig.SubmitButtonText
              }
              icon="pi pi-check"
              className=" btn btn-primary btn-primary-crud"
              type="submit"
              disabled={isLoading || registrationApproved}
            />
          </div>
        </form>
      </div>
    </React.Fragment>
  );
};

export default RegistrationsForm;
